import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableStatic';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { isArray, isEmpty, isUndefined, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import { Filters } from './components/Filters';
import configuration from './includes/configuration.json';
import { Styles } from './includes/styles';

/**
 * Table: Codes
 *
 * Displays a list of codes within a DataTable.
 *
 * @param assets
 * @param queries
 * @param setCurrentEntity
 * @param setIsFormModalOpen
 * @param setTheQueries
 * @returns {Element}
 * @constructor
 */
export function Codes({
  assets = {},
  queries = {},
  setCurrentEntity = () => {},
  setIsFormModalOpen = () => {},
  setTheQueries = () => {},
}) {
  const { hasPermission } = useCurrentUser();

  const canEdit = hasPermission('Admin:Codes', 'edit');

  /**
   * renderCellActions
   *
   * Provides the user with a menu of actionable items to perform on a specific action path within the list of action
   * paths.
   *
   * @param row
   * @returns {Element}
   */
  const renderCellActions = ({ row }) => {
    const menuItems = [
      {
        disabled: !canEdit,
        icon: <IconSymbol icon="edit" />,
        onClick: () => {
          setIsFormModalOpen(true);
          setCurrentEntity(row?.original);
        },
        title: 'Edit',
      },
    ];

    return (
      <div
        style={{
          marginLeft: '-10px',
          marginRight: '-10px',
        }}
      >
        <Layout.Group
          css={{
            height: '100%',
            marginLeft: '2px',
            marginRight: '2px',
          }}
        >
          <DropdownMenu
            before={<IconSymbol color="#6F6F6F" icon="more_vert" size={24} />}
            hideLabel
            label=""
            menuItems={menuItems}
          />
        </Layout.Group>
      </div>
    );
  };

  /**
   * renderCellIsValid
   *
   * display the value of the isValid field as a string
   *
   * @param value
   * @returns {string}
   */
  const renderCellIsValid = ({ value }) => {
    return String(value).toUpperCase();
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return orderBy(configuration?.initialColumns, ['order'], ['asc']).map((item) => {
      const column = item;

      if (column.accessor === 'actions') {
        column.Cell = renderCellActions;
      }

      if (column.accessor === 'isValid') {
        column.Cell = renderCellIsValid;
      }

      return column;
    });
  }, [canEdit]);

  /**
   * Each code category requires a separate api call. Combine all the data into a single array to be used as rows.
   */
  const rows = useMemo(() => {
    const theRows = [];

    if (!isUndefined(queries) && !isEmpty(queries)) {
      Object.keys(queries).forEach((queryKey) => {
        const query = queries[queryKey];

        if (!isEmpty(query?.data) && isArray(query?.data)) {
          query?.data?.forEach((item) => {
            theRows.push(item);
          });
        }
      });
    }

    return theRows;
  }, [queries]);

  return (
    <ErrorHandler location="src/entities/Code/ui/components/tables/Codes/Codes.jsx">
      <Styles>
        <TableComponent
          {...{
            columns,
            configuration,
            headerLeft: <Filters assets={assets} queries={queries} setTheQueries={setTheQueries} />,
            noDataMessage: 'No codes found with selected categories.',
            rows,
          }}
        />
      </Styles>
    </ErrorHandler>
  );
}
